// src/context/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import {
  AuthResponse,
  authService,
  UserProfile,
  GoogleUser,
} from "../services/auth.service";
import { tokenService, CustomerData } from "../services/token.service";

// Tipos para el contexto
interface AuthContextType {
  user: GoogleUser | null;
  customerData: CustomerData | null;
  userProfile: UserProfile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; unverified?: boolean }>;
  loginWithGoogle: (countryCode?: string) => Promise<{ success: boolean; error: string | null }>;
  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    phone: string,
    dni: string,
    dniType: string,
    countryCode?: string,
    razonSocial?: string
  ) => Promise<{ success: boolean; message: string | null }>;
  registerWithGoogle: (countryCode?: string) => Promise<{ success: boolean; error: string | null }>; // <--- MODIFICADO
  logout: () => Promise<void>;
  clearCustomerData: () => void;
  resetPassword: (email: string) => Promise<boolean>;
  updateProfile: (
    firstName: string,
    lastName: string,
    phone: string // Cambiado a 'phone'
  ) => Promise<{ success: boolean; error: string | null }>;
  completeProfile: (
    firstName: string,
    lastName: string,
    phone: string, // Cambiado a 'phone'
    dniType: string,
    dni: string
  ) => Promise<{ success: boolean; error: string | null }>;
  updatePassword: (
    oldPassword: string,
    newPassword: string
  ) => Promise<{ success: boolean; error: string | null }>;
  syncToken: () => void;
}

// Crear el contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider del contexto
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<GoogleUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  const [loading, setLoading] = useState(true);

  // Efecto para inicializar el estado de autenticación desde el token
  useEffect(() => {
    setLoading(true);

    // 1. Comprobar el token JWT primero
    const tokenData = tokenService.getCustomerDataToken();
    if (tokenData && tokenData.customerData) {
      setCustomerData(tokenData.customerData);
      
      // Intentar restaurar el usuario de Google de localStorage si existe
      const savedGoogleUser = localStorage.getItem("googleUser");
      if (savedGoogleUser) {
        try {
          setUser(JSON.parse(savedGoogleUser));
        } catch (e) {
          console.error("Error parsing saved Google User", e);
        }
      }
    }
    setLoading(false);
  }, []);

  // Función de login con email (enfocada en token)
  const login = async (email: string, password: string): Promise<{ success: boolean; unverified?: boolean }> => {
  try {
    const response = await authService.loginWithEmail(email, password);
    
    if (response.success && response.customerData) {
      // Si el backend devolvió el token pero no está verificado:
      /*
      if (!response.customerData.isVerify) {
        // OPCIONAL: Podrías borrar el token aquí si no quieres que se guarde
        // tokenService.removeToken(); 
        return { success: true, unverified: true };
      }
      */

      setCustomerData(response.customerData);
      return { success: true };
    }
    return { success: false };
  } catch (error) {
    return { success: false };
  }
};

  // Función de login con Google (sin Firebase)
  const loginWithGoogle = async (countryCode?: string): Promise<{
    success: boolean;
    error: string | null;
  }> => {
    try {
      const response = await authService.loginWithGoogle(countryCode);
      if (response.success && response.customerData && response.user) {
        setCustomerData(response.customerData);
        setUser(response.user);
        // Guardar en localStorage para persistencia
        localStorage.setItem("googleUser", JSON.stringify(response.user));
        return { success: true, error: null };
      }
      return { success: false, error: response.error };
    } catch (error: any) {
      console.error("Error en login con Google:", error);
      return { success: false, error: error.message || "Error inesperado" };
    }
  };

  // Función de registro
  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    phone: string,
    dni: string,
    dniType: string,
    countryCode?: string,
    razonSocial?: string
  ): Promise<{ success: boolean; message: string | null }> => {
    try {
      const response = await authService.registerWithEmail(
        email,
        password,
        firstName,
        lastName,
        phone,
        dni,
        dniType,
        countryCode,
        razonSocial
      );
      return { success: response.success, message: response.error };
    } catch (error) {
      console.error("Error en registro:", error);
      return {
        success: false,
        message: (error as Error).message || (error as string),
      };
    }
  };
  // Función de logout unificada
  const logout = async (): Promise<void> => {
    try {
      await authService.logout(); // Limpia la sesión y el token en localStorage
      localStorage.removeItem("googleUser");
    } catch (error) {
      console.error("Error en logout:", error);
    } finally {
      // Siempre limpiamos el estado local al final
      setUser(null);
      setUserProfile(null);
      setCustomerData(null);
    }
  };

  const clearCustomerData = (): void => {
    tokenService.removeToken();
    localStorage.removeItem("authToken");
    localStorage.removeItem("isVerify");
    localStorage.removeItem("googleUser");
    setUser(null);
    setUserProfile(null);
    setCustomerData(null);
  };

  // Función de recuperar contraseña
  const resetPassword = async (email: string): Promise<boolean> => {
    try {
      const response = await authService.resetPassword(email);
      return response.success;
    } catch (error) {
      console.error("Error al enviar email de recuperación:", error);
      return false;
    }
  };

  // Función para actualizar el perfil del usuario
  const updateProfile = async (
    firstName: string,
    lastName: string,
    phone: string // Cambiado a 'phone'
  ): Promise<{ success: boolean; error: string | null }> => {
    try {
      const response = await authService.updateCustomerProfile(
        firstName,
        lastName,
        phone // Cambiado a 'phone'
      );

      if (response.success) {
        // Si la actualización en el backend fue exitosa, actualizamos customerData en el contexto
        // Re-leer el token para obtener los datos más recientes
        const updatedTokenData = tokenService.getCustomerDataToken();
        if (updatedTokenData && updatedTokenData.customerData) {
          setCustomerData(updatedTokenData.customerData);
        } else {
          // Si no se pudo obtener el token actualizado, forzar un re-fetch o manejar el error
          console.warn("No se pudo obtener customerData actualizado después de la actualización del perfil.");
        }
      }
      return response;
    } catch (error: any) {
      console.error("Error al actualizar el perfil en el contexto:", error);
      return { success: false, error: error.message || "Error inesperado al actualizar el perfil." };
    }
  };

  // Función para completar el perfil del usuario (incluyendo DNI y tipo de DNI)
  const completeProfile = async (
    firstName: string,
    lastName: string,
    phone: string, // Cambiado a 'phone'
    dniType: string,
    dni: string
  ): Promise<{ success: boolean; error: string | null }> => {
    try {
      const response = await authService.completeCustomerProfile(
        firstName,
        lastName,
        phone, // Cambiado a 'phone'
        dniType,
        dni
      );

      if (response.success) {
        // Si la actualización en el backend fue exitosa, actualizamos customerData en el contexto
        // Re-leer el token para obtener los datos más recientes
        const updatedTokenData = tokenService.getCustomerDataToken();
        if (updatedTokenData && updatedTokenData.customerData) {
          setCustomerData(updatedTokenData.customerData);
        } else {
          console.warn("No se pudo obtener customerData actualizado después de completar el perfil.");
        }
      }
      return response;
    } catch (error: any) {
      console.error("Error al completar el perfil en el contexto:", error);
      return { success: false, error: error.message || "Error inesperado al completar el perfil." };
    }
  };

  const syncToken = () => {
    const tokenData = tokenService.getCustomerDataToken();
    if (tokenData && tokenData.customerData) {
      setCustomerData(tokenData.customerData);
    }
  };

  const updatePassword = async (
    oldPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; error: string | null }> => {
    try {
      const response = await authService.updateCustomerPassword(
        oldPassword,
        newPassword
      );
      return response;
    } catch (error: any) {
      console.error("Error al actualizar la contraseña en el contexto:", error);
      return {
        success: false,
        error:
          error.message || "Error inesperado al actualizar la contraseña.",
      };
    }
  };

  // Función de registro con Google (unificada con login)
  const registerWithGoogle = async (countryCode?: string): Promise<{
    success: boolean;
    error: string | null;
  }> => {
    return loginWithGoogle(countryCode);
  };

  const value: AuthContextType = {
    user,
    customerData,
    userProfile,
    loading,
    login,
    loginWithGoogle,
    register,
    registerWithGoogle, // <--- AÑADIDO
    logout,
    clearCustomerData,
    resetPassword,
    updateProfile,
    completeProfile,
    updatePassword,
    syncToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook personalizado para usar el contexto de autenticación
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth debe ser usado dentro de un AuthProvider");
  }

  return context;
};
